import React, { useEffect, useState } from "react";
import { Button, Modal, message, Select, Tag } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CalendarOutlined,
    PhoneOutlined,
    CheckOutlined,
    CloseOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import Paginationnew from "../../Uitiles/Paginationnew";

const DiagnosticAppointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [filterStatus, setFilterStatus] = useState('all');

    // Form states
    const [formData, setFormData] = useState({
        patient_name: '',
        phone: '',
        test_name: '',
        appointment_date: '',
        appointment_time: '',
        status: 'pending',
        notes: ''
    });

    const statusOptions = ['pending', 'confirmed', 'completed', 'cancelled'];

    useEffect(() => {
        loadAppointments();
    }, [page, searchText, filterStatus]);

    const loadAppointments = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            let conditions = { diagnostic_id: userId };

            if (filterStatus !== 'all') {
                conditions.status = filterStatus;
            }

            const search = searchText ? {
                patient_name: searchText,
                phone: searchText,
                test_name: searchText
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'diagnostic_appointments', conditions, 'id DESC', search, pagination);
            if (response && response.data) {
                setAppointments(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Error loading appointments:', error);
            message.error('Failed to load appointments');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!formData.patient_name || !formData.phone || !formData.test_name || !formData.appointment_date) {
                message.error('Please fill in all required fields');
                return;
            }

            const userId = await getuserdata('id');
            const appointmentData = {
                ...formData,
                diagnostic_id: userId
            };

            if (editingAppointment) {
                await oneKlicks.update('', 'diagnostic_appointments', appointmentData, { id: editingAppointment.id });
                message.success('Appointment updated successfully');
            } else {
                await oneKlicks.create('', 'diagnostic_appointments', appointmentData);
                message.success('Appointment added successfully');
            }

            setModalVisible(false);
            resetForm();
            loadAppointments();
        } catch (error) {
            console.error('Error saving appointment:', error);
            message.error('Failed to save appointment');
        }
    };

    const resetForm = () => {
        setFormData({
            patient_name: '',
            phone: '',
            test_name: '',
            appointment_date: '',
            appointment_time: '',
            status: 'pending',
            notes: ''
        });
        setEditingAppointment(null);
    };

    const handleEdit = (appointment) => {
        setEditingAppointment(appointment);
        setFormData({
            patient_name: appointment.patient_name || '',
            phone: appointment.phone || '',
            test_name: appointment.test_name || '',
            appointment_date: appointment.appointment_date || '',
            appointment_time: appointment.appointment_time || '',
            status: appointment.status || 'pending',
            notes: appointment.notes || ''
        });
        setModalVisible(true);
    };

    const handleDelete = async (appointment) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this appointment for "${appointment.patient_name}"?`);
        if (confirmDelete) {
            try {
                await oneKlicks.delete('', 'diagnostic_appointments', { id: appointment.id });
                message.success('Appointment deleted successfully');
                loadAppointments();
            } catch (error) {
                console.error('Error deleting appointment:', error);
                message.error('Failed to delete appointment');
            }
        }
    };

    const handleStatusChange = async (appointment, newStatus) => {
        try {
            await oneKlicks.update('', 'diagnostic_appointments', { status: newStatus }, { id: appointment.id });
            message.success(`Status changed to ${newStatus}`);
            loadAppointments();
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            confirmed: 'blue',
            completed: 'green',
            cancelled: 'red'
        };
        return colors[status] || 'default';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">
                                    <CalendarOutlined className="me-2" />
                                    Appointments Management
                                </h5>
                                <Badge bg="primary">Appointments</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40" style={{ maxWidth: '300px' }}>
                                    <Form.Control
                                        placeholder="Search by patient, phone, test..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </InputGroup>
                                <Select
                                    style={{ width: 150 }}
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    options={[
                                        { value: 'all', label: 'All Status' },
                                        { value: 'pending', label: 'Pending' },
                                        { value: 'confirmed', label: 'Confirmed' },
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'cancelled', label: 'Cancelled' }
                                    ]}
                                />
                                <Button className="btn-primary h-40" onClick={() => {
                                    resetForm();
                                    setModalVisible(true);
                                }}>
                                    <PlusOutlined /> Add Appointment
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Patient</th>
                                        <th>Test</th>
                                        <th>Date & Time</th>
                                        <th>Status</th>
                                        <th style={{ width: '200px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : appointments.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">No appointments found</td>
                                        </tr>
                                    ) : (
                                        appointments.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID">{item.id}</td>
                                                <td data-label="Patient">
                                                    <div>
                                                        <strong>{item.patient_name}</strong>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            <PhoneOutlined className="me-1" />{item.phone}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td data-label="Test">
                                                    <Badge bg="info">{item.test_name}</Badge>
                                                </td>
                                                <td data-label="Date & Time">
                                                    <div>
                                                        <CalendarOutlined className="me-1" />
                                                        {formatDate(item.appointment_date)}
                                                        {item.appointment_time && (
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                <ClockCircleOutlined className="me-1" />{item.appointment_time}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td data-label="Status">
                                                    <Tag color={getStatusColor(item.status)}>
                                                        {item.status?.toUpperCase() || 'PENDING'}
                                                    </Tag>
                                                </td>
                                                <td data-label="Actions">
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                        {item.status === 'pending' && (
                                                            <Button
                                                                className="btn-success"
                                                                onClick={() => handleStatusChange(item, 'confirmed')}
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            >
                                                                <CheckOutlined />
                                                            </Button>
                                                        )}
                                                        {item.status === 'confirmed' && (
                                                            <Button
                                                                className="btn-success"
                                                                onClick={() => handleStatusChange(item, 'completed')}
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            >
                                                                Complete
                                                            </Button>
                                                        )}
                                                        {(item.status === 'pending' || item.status === 'confirmed') && (
                                                            <Button
                                                                className="btn-warning"
                                                                onClick={() => handleStatusChange(item, 'cancelled')}
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            >
                                                                <CloseOutlined />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            className="btn-info"
                                                            onClick={() => handleEdit(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <EditOutlined />
                                                        </Button>
                                                        <Button
                                                            className="btn-danger"
                                                            onClick={() => handleDelete(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <DeleteOutlined />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Add/Edit Modal */}
                <Modal
                    title={editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        resetForm();
                    }}
                    onOk={handleSubmit}
                    width={700}
                >
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Patient Name *</label>
                            <Form.Control
                                placeholder="Enter patient name"
                                value={formData.patient_name}
                                onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Phone *</label>
                            <Form.Control
                                placeholder="Enter phone number"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Test Name *</label>
                            <Form.Control
                                placeholder="Enter test name"
                                value={formData.test_name}
                                onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Status</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.status}
                                onChange={(value) => setFormData({ ...formData, status: value })}
                                options={statusOptions.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Appointment Date *</label>
                            <Form.Control
                                type="date"
                                value={formData.appointment_date}
                                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Appointment Time</label>
                            <Form.Control
                                type="time"
                                value={formData.appointment_time}
                                onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                            />
                        </div>
                        <div className="col-12 mb-3">
                            <label className="form-label">Notes</label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Additional notes..."
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default DiagnosticAppointments;
